import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AppConfig } from 'src/app-config.model';

/**
 * Aplica las variables inyectadas por docker-entrypoint.sh en window.__ENV__ al objeto environment.
 * Debe ejecutarse en main.ts antes de fetchPublicAppConfig.
 * En builds nativos (Capacitor) no tiene efecto; los valores se configuran en environment.apps.*.ts.
 */
export function applyWindowEnv(): void {
  const win = globalThis as unknown as { __ENV__?: Record<string, string> };
  const e = win.__ENV__;
  if (!e) return;

  if (e['API_CMS_URL']) environment.endpointBasePaths.cmsApiUrl = e['API_CMS_URL'];
  if (e['INFO_API_URL']) environment.endpointBasePaths.infoAPIUrl = e['INFO_API_URL'];
  if (e['BETS_API_URL']) environment.endpointBasePaths.gameApiUrl = e['BETS_API_URL'];
  if (e['KEYCLOAK_URL']) environment.keycloak.testing.url = e['KEYCLOAK_URL'];
  if (e['KEYCLOAK_REALM']) environment.keycloak.testing.realm = e['KEYCLOAK_REALM'];
  if (e['KEYCLOAKCLIENT_ID']) environment.keycloak.testing.clientId = e['KEYCLOAKCLIENT_ID'];
  if (e['RECAPTCHA_V3_SITE_KEY']) environment.recaptchaConfig.siteKey = e['RECAPTCHA_V3_SITE_KEY'];
  if (e['PAYMENTMETHODSSRCURL']) environment.paymentMethodsSrc.base_url = e['PAYMENTMETHODSSRCURL'];
  if (e['PAYMENT_METHODS_REDIRECT_URL']) environment.paymentMethodsSrc.redirect_url = e['PAYMENT_METHODS_REDIRECT_URL'];
  if (e['URL_JUEGO_REAL_RV']) environment.game.eRaspadita.urlJuegoReal = e['URL_JUEGO_REAL_RV'];
}

@Injectable({
  providedIn: 'root',
})
export class EnvironmentService {
  /**
   * Aplica los valores del AppConfig (obtenidos del CMS en el bootstrap) sobre el objeto environment.
   */
  applyConfig(config: AppConfig): void {
    const overrides: Array<[unknown, (v: string) => void]> = [
      [
        config.APIURL,
        (v) => {
          environment.endpointBasePaths.userApiUrl = v;
        },
      ],
      [
        config.MAX_BETS_PER_GAME_TYPE,
        (v) => {
          environment.game.cart.maxBetsPerGameType = +v;
        },
      ],
      [
        config.LUDOPATIA_MAX_TIME,
        (v) => {
          environment.ludopatia.maxTime = +v;
        },
      ],
      [
        config.LUDOPATIA_LINK,
        (v) => {
          environment.ludopatia.link = v;
        },
      ],
      [
        config.LUDOPATIA_SESSION_WARNING_MINUTES,
        (v) => {
          environment.ludopatia.sessionWarningMinutes = +v;
        },
      ],
      [
        config.LUDOPATIA_SESSION_WARNING_MESSAGE,
        (v) => {
          environment.ludopatia.sessionWarningMessage = v;
        },
      ],
      [
        config.LUDOPATIA_TUTORIAL_URL,
        (v) => {
          environment.ludopatia.tutorialUrl = v;
        },
      ],
      [
        config.PAYMENT_METHODS_ICONS_BASE_URL,
        (v) => {
          environment.paymentMethodsSrc.iconsBaseUrl = v;
        },
      ],
      [
        config.GOOGLE_MAPS_API_KEY,
        (v) => {
          environment.googleMapsApiKey = v;
        },
      ],
      [
        config.GOOGLE_TAG_MANAGER_ID,
        (v) => {
          environment.googleTagManagerId = v;
        },
      ],
      [
        config.HEYNOW_ENABLED,
        (v) => {
          environment.heynow.enabled = v.toLowerCase() === 'true' || v === '1' || v.toLowerCase() === 'yes';
        },
      ],
      [
        config.HEYNOW_PLATFORM_ID,
        (v) => {
          environment.heynow.platformId = v;
        },
      ],
      [
        config.HEYNOW_SCRIPT_URL,
        (v) => {
          environment.heynow.scriptUrl = v;
        },
      ],
      [
        config.SIMPLE_TECH_NOTIFICATIONS_WIDGET_TOKEN,
        (v) => {
          if (v.trim().toLowerCase() === 'none') return;
          environment.simpleTechNotificationsWidgetToken = v;
        },
      ],
      [
        config.SIMPLE_TECH_NOTIFICATIONS_SCRIPT_URL,
        (v) => {
          environment.simpleTechNotificationsScriptUrl = v;
        },
      ],
      [
        config.CANALES_VENTA_APPS_ANDROID,
        (v) => {
          environment.canalesVenta.apps.android = v;
        },
      ],
      [
        config.CANALES_VENTA_APPS_IOS,
        (v) => {
          environment.canalesVenta.apps.ios = v;
        },
      ],
      [
        config.SEGUNDA_CHANCE_TYC_SLUG,
        (v) => {
          environment.segundaChanceTycSlug = v;
        },
      ],
    ];

    for (const [value, apply] of overrides) {
      if (isSet(value)) apply(value);
    }
  }
}

function isSet(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

export function initializeEnvironment(environmentService: EnvironmentService, config: AppConfig): () => void {
  return () => environmentService.applyConfig(config);
}
